/**
 * Operator scope selector model (W1-ADMIN, D358; STATE-SCOPE S2, ADR-0124) — the fusion of the operator's **grants** (the
 * authority) with the registered **market catalog** (the cities inside that authority) and the **region catalog** (the
 * states). The hierarchy is Global > Country > State > City. It retires the D337 `humanizeSlug` shortcut, which
 * put a display name (`Patos De Minas`) where RBAC compares the grant `scopeId` (`br:patos-de-minas`): a city-scoped
 * operator was refused on every scoped read and a country+city pair failed the server's scope-consistency check.
 *
 * Rules (pure, tested in Node):
 * - A city scope's value is always the canonical market id `cc:slug`; its display name comes from the catalog. A city
 *   scope is `{ country, city }` - it never carries its state.
 * - A state scope is `{ country, region }` where `region` is the ISO 3166-2 code (`BR-MG`); it means "the markets whose
 *   `regionCode` is that state" (`marketsOfScope`).
 * - `GLOBAL` authorizes the global scope plus every registered country, its ACTIVE states and its markets.
 * - `COUNTRY cc` authorizes that country, its ACTIVE states and every registered market in it.
 * - `REGION cc-xx` authorizes that state and the registered markets whose `regionCode` is it. It does **not** add the
 *   country: RBAC would refuse a country-level read for a state grant.
 * - `CITY`/`MARKET cc:slug` authorizes exactly that market (even if the catalog does not list it — the grant is the
 *   authority). It does **not** add the whole-country scope: RBAC would refuse a country-level read for a city grant.
 * - Other grant scope kinds (merchant, fleet, …) are not geographic and are ignored here.
 * - A `RETIRED` market (or state) is only offered when a grant names it explicitly.
 * - The initial scope is global when authorized, else the first authorized country, else the first authorized state,
 *   else the first authorized city — never a scope the operator cannot read. The console then narrows it to where the
 *   browser is (`preferredScope`).
 * The server re-authorizes every call; this model only keeps the console from offering what RBAC would refuse.
 */
import { type AdministrativeRegionView, type OperatingMarketView } from '@vello/api-client';
import { type OperatorScope, type ScopeState } from './shell.js';

/** The grant shape the selector needs (structurally `OperatorGrant`, open to future scope kinds). */
export interface ScopeGrant {
  readonly role: string;
  readonly scopeType: string;
  readonly scopeId?: string;
}

const COUNTRY = /^[A-Za-z]{2}$/;
const MARKET_ID = /^[a-z]{2}:[a-z0-9]+(?:-[a-z0-9]+)*$/;
/** An ISO 3166-2 first-level subdivision code: the country, a hyphen, 1-3 alphanumerics (`BR-MG`). */
const REGION_CODE = /^[A-Za-z]{2}-[A-Za-z0-9]{1,3}$/;

/** The country (upper-case alpha-2) a canonical `cc:slug` market id belongs to. */
export function countryOfMarket(marketId: string): string {
  return marketId.slice(0, 2).toUpperCase();
}

/** The canonical (upper-case) form of a region code, or `undefined` when it is not an ISO 3166-2 first-level code. */
export function normalizeRegionCode(code: string | undefined): string | undefined {
  const c = code?.trim().toUpperCase();
  return c !== undefined && REGION_CODE.test(c) ? c : undefined;
}

/** The country (upper-case alpha-2) a region code (`BR-MG`) belongs to: its prefix. */
export function countryOfRegion(regionCode: string): string {
  return regionCode.slice(0, 2).toUpperCase();
}

/** The part of a region code after the country (`BR-MG` → `MG`): the short label of a state. */
export function regionSuffix(regionCode: string): string {
  const i = regionCode.indexOf('-');
  return (i >= 0 ? regionCode.slice(i + 1) : regionCode).toUpperCase();
}

/** A market's display name from the catalog, or its id when the catalog does not list it. */
export function marketDisplayName(marketId: string, markets: readonly OperatingMarketView[]): string {
  return markets.find((m) => m.id === marketId)?.name ?? marketId;
}

/**
 * A market's name for a chart label or a list: the registered name, else the slug of its id read as words (`br:sao-paulo` →
 * `Sao Paulo`), so a market without a registered name never prints a raw `cc:slug` id (D854, L7a-006). An id with no slug stays as it is.
 */
export function marketReadableName(marketId: string, markets: readonly OperatingMarketView[]): string {
  const named = markets.find((m) => m.id === marketId)?.name;
  if (named !== undefined && named !== '') return named;
  const slug = marketId.includes(':') ? marketId.slice(marketId.indexOf(':') + 1) : '';
  const words = slug.split(/[-_]+/).filter((w) => w !== '');
  return words.length === 0 ? marketId : words.map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

/** A state's display name from the region catalog, or the suffix of its code (`BR-MG` → `MG`) when the catalog does not list it. */
export function regionDisplayName(regionCode: string, regions: readonly AdministrativeRegionView[]): string {
  const code = regionCode.toUpperCase();
  return regions.find((r) => r.code.toUpperCase() === code)?.name ?? regionSuffix(code);
}

/** Whether a scope names a state (a region with no city under it): "a country restricted to that state's markets". */
export function isRegionScope(scope: OperatorScope): boolean {
  return scope.region !== undefined && scope.city === undefined;
}

/** Whether two region codes name the same state (codes compare case-insensitively). */
export function sameRegion(a: string | undefined, b: string | undefined): boolean {
  return a !== undefined && b !== undefined && a.toUpperCase() === b.toUpperCase();
}

/** The state a registered market belongs to (its `regionCode`, upper-case), or `undefined`. */
export function regionOfMarket(marketId: string, markets: readonly OperatingMarketView[]): string | undefined {
  return markets.find((m) => m.id === marketId)?.regionCode?.toUpperCase();
}

/**
 * The markets of a state: the registered, non-retired markets whose `regionCode` is it. A state with no market in the
 * catalog answers `[]` - and a scope on it then reaches nothing, never "the whole country".
 */
export function marketsOfRegion(regionCode: string, markets: readonly OperatingMarketView[]): readonly OperatingMarketView[] {
  return markets.filter((m) => MARKET_ID.test(m.id) && m.status !== 'RETIRED' && sameRegion(m.regionCode, regionCode));
}

/**
 * The market ids a scope reaches in the catalog: a city → that market; a state → the markets of the state; a country → its
 * registered, non-retired markets; global → `undefined` (no restriction). **The one answer to "which markets is this
 * scope" for a state**, so every screen that cannot ask the server for a region restricts the same way.
 */
export function marketsOfScope(scope: OperatorScope, markets: readonly OperatingMarketView[]): readonly string[] | undefined {
  if (scope.city !== undefined && scope.city !== '') return [scope.city];
  if (scope.region !== undefined) return marketsOfRegion(scope.region, markets).map((m) => m.id);
  if (scope.country !== undefined && scope.country !== '') {
    const cc = scope.country.toUpperCase();
    return markets.filter((m) => MARKET_ID.test(m.id) && m.status !== 'RETIRED' && countryOfMarket(m.id) === cc).map((m) => m.id);
  }
  return undefined;
}

/** Whether a market belongs to a scope: global → yes; country → same country; state → registered in that state; city → that market. */
export function marketInScope(marketId: string | undefined, scope: OperatorScope, markets: readonly OperatingMarketView[]): boolean {
  if (marketId === undefined) return scope.country === undefined && scope.region === undefined && scope.city === undefined;
  if (scope.city !== undefined) return marketId === scope.city;
  if (scope.region !== undefined) return sameRegion(regionOfMarket(marketId, markets), scope.region);
  if (scope.country !== undefined) return countryOfMarket(marketId) === scope.country.toUpperCase();
  return true;
}

/**
 * The countries the operator reaches, upper-case and sorted: each allowed scope's country (or the prefix of its state / city); a global scope reaches every
 * country the catalogs know (a registered market's, a registered state's). The campaign screens offer exactly these where the top scope names no country,
 * instead of a two-letter text field.
 */
export function reachableCountries(allowed: readonly OperatorScope[], markets: readonly OperatingMarketView[], regions: readonly AdministrativeRegionView[] = []): readonly string[] {
  const out = new Set<string>();
  for (const s of allowed) {
    if (s.country !== undefined && s.country !== '') out.add(s.country.toUpperCase());
    else if (s.region !== undefined) out.add(countryOfRegion(s.region));
    else if (s.city !== undefined && s.city !== '') out.add(countryOfMarket(s.city));
    else {
      for (const m of markets) if (MARKET_ID.test(m.id) && m.status !== 'RETIRED') out.add(countryOfMarket(m.id));
      for (const r of regions) out.add(r.countryCode.toUpperCase());
    }
  }
  return [...out].sort();
}

function scopeKey(s: OperatorScope): string {
  return `${s.country ?? ''}|${s.region ?? ''}|${s.city ?? ''}`;
}

/** Fuse grants (authority), the market catalog (cities) and the region catalog (states) into the selector's scope set. */
export function scopesFromGrantsAndMarkets(grants: readonly ScopeGrant[], markets: readonly OperatingMarketView[], regions: readonly AdministrativeRegionView[] = []): ScopeState {
  let global = false;
  const countries = new Set<string>();
  const states = new Set<string>();
  const cities = new Set<string>();
  const explicitCities = new Set<string>();
  const explicitStates = new Set<string>();

  const catalog = markets.filter((m) => MARKET_ID.test(m.id));
  const listed = (m: OperatingMarketView): boolean => m.status !== 'RETIRED';
  const stateCatalog = regions.filter((r) => normalizeRegionCode(r.code) !== undefined);
  const activeStates = stateCatalog.filter((r) => r.status === 'ACTIVE').map((r) => r.code.toUpperCase());

  for (const g of grants) {
    const type = g.scopeType.toUpperCase();
    const id = g.scopeId?.trim() ?? '';
    if (type === 'GLOBAL') {
      global = true;
    } else if (type === 'COUNTRY' && COUNTRY.test(id)) {
      countries.add(id.toUpperCase());
    } else if (type === 'REGION' && normalizeRegionCode(id) !== undefined) {
      explicitStates.add(id.toUpperCase());
    } else if ((type === 'CITY' || type === 'MARKET') && MARKET_ID.test(id)) {
      explicitCities.add(id);
    }
  }

  if (global) {
    for (const m of catalog.filter(listed)) {
      countries.add(countryOfMarket(m.id));
      cities.add(m.id);
    }
    for (const code of activeStates) countries.add(countryOfRegion(code));
  }
  for (const cc of countries) {
    for (const m of catalog.filter(listed)) if (countryOfMarket(m.id) === cc) cities.add(m.id);
    for (const code of activeStates) if (countryOfRegion(code) === cc) states.add(code);
  }
  for (const code of explicitStates) {
    states.add(code);
    for (const m of marketsOfRegion(code, catalog)) cities.add(m.id);
  }
  for (const id of explicitCities) cities.add(id);

  const allowed: OperatorScope[] = [];
  const seen = new Set<string>();
  const push = (s: OperatorScope): void => {
    const k = scopeKey(s);
    if (!seen.has(k)) {
      seen.add(k);
      allowed.push(s);
    }
  };
  if (global) push({});
  const byName = (a: string, b: string): number => marketDisplayName(a, catalog).localeCompare(marketDisplayName(b, catalog));
  const byState = (a: string, b: string): number => regionDisplayName(a, stateCatalog).localeCompare(regionDisplayName(b, stateCatalog));
  const countryOrder = [...new Set([...countries, ...[...states].map(countryOfRegion), ...[...cities].map(countryOfMarket)])].sort();
  for (const cc of countryOrder) {
    if (countries.has(cc)) push({ country: cc });
    for (const code of [...states].filter((r) => countryOfRegion(r) === cc).sort(byState)) push({ country: cc, region: code });
    for (const id of [...cities].filter((c) => countryOfMarket(c) === cc).sort(byName)) push({ country: cc, city: id });
  }

  if (allowed.length === 0) {
    // No geographic authority at all: keep the console usable (every privileged call is still refused server-side).
    return { current: {}, allowed: [{}] };
  }
  const current = global ? {} : (allowed.find((s) => s.city === undefined) ?? allowed[0] ?? {});
  return { current, allowed };
}

/** Whether a scope is one the operator may switch to (used to hide choices RBAC would refuse). */
export function scopeAllowed(state: ScopeState, target: OperatorScope): boolean {
  return state.allowed.some((s) => s.country === target.country && s.region === target.region && s.city === target.city);
}

// ---- the selector's menu: country > state > city ----------------------------------------------------------------------

/** One state group of a country in the selector. `region` is `undefined` for the group of cities that have no (selectable or listed) state. */
export interface ScopeMenuState {
  readonly region?: string;
  /** Whether the grants reach the whole state (the "whole state" row is offered only then). */
  readonly allowed: boolean;
  /** The market ids under it, in the scope set's order. */
  readonly cities: readonly string[];
}

export interface ScopeMenuCountry {
  readonly country: string;
  /** Whether the grants reach the whole country (the "whole country" row is offered only then). */
  readonly allowed: boolean;
  /** The state groups, by state name; the group without a state (when any city has none) comes last. Empty when the country has no state level at all. */
  readonly states: readonly ScopeMenuState[];
  /** The cities of a country with no state level (the two-column layout). Empty when `states` is not. */
  readonly cities: readonly string[];
}

export interface ScopeMenuModel {
  readonly countries: readonly ScopeMenuCountry[];
  /** Whether any country has a state level - the menu then shows three columns. */
  readonly hasStates: boolean;
  /** Whether any country has cities (below the countries or below its states) - else the menu is one column. */
  readonly hasCities: boolean;
}

/**
 * The selector's menu model from the scope set (`ScopeState.allowed`): countries, each with its states (selectable ones from the grants,
 * plus the state of any reachable city that the catalog names) and the cities under them. A city whose state is not in the region
 * catalog - or a market with no `regionCode` - sits in the group without a state, so every reachable city stays reachable.
 */
export function scopeMenuModel(allowed: readonly OperatorScope[], markets: readonly OperatingMarketView[], regions: readonly AdministrativeRegionView[]): ScopeMenuModel {
  const byCountry = new Map<string, OperatorScope[]>();
  for (const s of allowed) if (s.country !== undefined) byCountry.set(s.country, [...(byCountry.get(s.country) ?? []), s]);
  const named = new Set(regions.map((r) => r.code.toUpperCase()));
  const countries: ScopeMenuCountry[] = [];
  for (const [country, scopes] of byCountry) {
    const allowedStates = new Set(scopes.filter((s) => s.region !== undefined && s.city === undefined).map((s) => (s.region as string).toUpperCase()));
    const cityIds = scopes.map((s) => s.city).filter((c): c is string => c !== undefined);
    const groups = new Map<string, string[]>();
    for (const code of allowedStates) groups.set(code, []);
    for (const id of cityIds) {
      const region = regionOfMarket(id, markets);
      const key = region !== undefined && (named.has(region) || allowedStates.has(region)) ? region : '';
      groups.set(key, [...(groups.get(key) ?? []), id]);
    }
    const stateGroups = [...groups.keys()].filter((k) => k !== '');
    const hasStates = stateGroups.length > 0;
    const states: ScopeMenuState[] = hasStates
      ? [
          ...stateGroups
            .sort((a, b) => regionDisplayName(a, regions).localeCompare(regionDisplayName(b, regions)))
            .map((code) => ({ region: code, allowed: allowedStates.has(code), cities: groups.get(code) ?? [] })),
          ...((groups.get('') ?? []).length > 0 ? [{ allowed: false, cities: groups.get('') ?? [] }] : []),
        ]
      : [];
    countries.push({ country, allowed: scopes.some((s) => s.region === undefined && s.city === undefined), states, cities: hasStates ? [] : cityIds });
  }
  return { countries, hasStates: countries.some((c) => c.states.length > 0), hasCities: countries.some((c) => c.cities.length > 0 || c.states.some((s) => s.cities.length > 0)) };
}

// ---- the starting scope: where the browser is (owner, 2026-10-12) ------------------------------------------------------

/** What the browser says about where it is. Neither value ever leaves the device. */
export interface ScopeLocationHint {
  /** The browser's position (WGS84), when the operator allowed the location prompt. */
  readonly position?: { readonly latitude: number; readonly longitude: number };
  /** The browser's IANA time zone (`Intl.DateTimeFormat().resolvedOptions().timeZone`). */
  readonly timeZone?: string;
}

/** How close (meters) the browser must be to a market's center for that city to be the starting scope. */
export const NEAR_MARKET_METERS = 60_000;

const EARTH_RADIUS_METERS = 6_371_008.8;

/** Great-circle distance in meters between two WGS84 points (haversine). */
export function distanceMeters(a: { readonly latitude: number; readonly longitude: number }, b: { readonly latitude: number; readonly longitude: number }): number {
  const rad = (deg: number): number => (deg * Math.PI) / 180;
  const dLat = rad(b.latitude - a.latitude);
  const dLon = rad(b.longitude - a.longitude);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.latitude)) * Math.cos(rad(b.latitude)) * Math.sin(dLon / 2) ** 2;
  return 2 * EARTH_RADIUS_METERS * Math.asin(Math.min(1, Math.sqrt(h)));
}

/**
 * The scope the console starts in — never global by default (owner, 2026-10-12):
 *   1. the authorized city whose market center is nearest the browser, within `NEAR_MARKET_METERS`;
 *   2. else the state the browser is in when only the state is authorized (no country entry), else the country the browser is in, when authorized: the country of the registered market nearest the browser
 *      (within the same radius), or the ONE country whose registered markets run in the browser's time zone;
 *   3. else what `scopesFromGrantsAndMarkets` chose (global for a global operator browsing from where no market exists).
 * Only scopes in `state.allowed` are ever returned. Pure: the browser reads happen in the web layer.
 */
export function preferredScope(state: ScopeState, markets: readonly OperatingMarketView[], hint: ScopeLocationHint): OperatorScope {
  const allowed = (s: OperatorScope): boolean => scopeAllowed(state, s);
  const listed = markets.filter((m) => MARKET_ID.test(m.id) && m.status !== 'RETIRED');
  const position = hint.position;
  let nearCountry: string | undefined;
  if (position !== undefined) {
    const near = listed
      .filter((m) => m.center !== undefined)
      .map((m) => ({ m, d: distanceMeters(position, m.center as { latitude: number; longitude: number }) }))
      .filter((x) => x.d <= NEAR_MARKET_METERS)
      .sort((a, b) => a.d - b.d);
    const city = near.find((x) => allowed({ country: countryOfMarket(x.m.id), city: x.m.id }));
    if (city !== undefined) return { country: countryOfMarket(city.m.id), city: city.m.id };
    nearCountry = near[0] !== undefined ? countryOfMarket(near[0].m.id) : undefined;
    // A state grant (no country entry): the state of the nearest market, when authorized, before the time-zone guess.
    const nearRegion = near[0]?.m.regionCode?.toUpperCase();
    if (nearCountry !== undefined && nearRegion !== undefined && !allowed({ country: nearCountry }) && allowed({ country: nearCountry, region: nearRegion })) return { country: nearCountry, region: nearRegion };
  }
  const zoneCountries = hint.timeZone === undefined ? [] : [...new Set(listed.filter((m) => m.timezone === hint.timeZone).map((m) => countryOfMarket(m.id)))];
  const country = nearCountry ?? (zoneCountries.length === 1 ? zoneCountries[0] : undefined);
  if (country !== undefined && allowed({ country })) return { country };
  return state.current;
}
